import React, { useCallback, useEffect, useState } from 'react';
import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Link2, ShieldCheck, ShieldAlert, Loader2, Download, Anchor, Lock } from 'lucide-react';
import { toast } from 'sonner';
import { useApi, useAuth } from '@/contexts/AuthContext';

const C = { navy: '#0D1B2A', slate: '#5C6B7A', green: '#1D9E75', amber: '#E8A020', red: '#E24B4A', blue: '#1A4C7C' };
const num = (v) => Number(v || 0).toLocaleString();

function Chip({ text, color, testid }) {
  return <span data-testid={testid} className="px-2 py-0.5 rounded text-[10px] font-bold uppercase tracking-wide"
    style={{ background: color + '15', color }}>{text}</span>;
}

function Hash({ value, testid }) {
  return <span data-testid={testid} title={value} className="font-mono text-[11px]" style={{ color: C.navy }}>
    {String(value || '').slice(0, 20)}…
  </span>;
}

function Stat({ label, value, color, testid }) {
  return (
    <div>
      <p className="text-lg font-bold font-mono leading-none" style={{ color: color || C.navy }} data-testid={testid}>{value}</p>
      <p className="text-[10px] uppercase tracking-wider mt-1" style={{ color: C.slate }}>{label}</p>
    </div>
  );
}

function VerifyResult({ result }) {
  const ok = result.intact;
  return (
    <div className="mt-3 p-3 rounded-sm" style={{ background: ok ? '#E7F6EF' : '#FDF1F1' }}
      data-testid={`verify-result-${result.collection}`}>
      <div className="flex items-center gap-2 mb-1.5">
        {ok ? <ShieldCheck className="w-4 h-4" style={{ color: C.green }} /> : <ShieldAlert className="w-4 h-4" style={{ color: C.red }} />}
        <p className="text-xs font-semibold" style={{ color: ok ? C.green : C.red }} data-testid={`verify-verdict-${result.collection}`}>
          {ok ? 'Chain reconciles — every row hashes to the row before it' : `${result.break_count} row${result.break_count === 1 ? '' : 's'} do not reconcile`}
        </p>
      </div>
      <p className="text-[11px]" style={{ color: C.navy }}>
        {num(result.rows_checked)} rows checked from position {num(result.verified_from_seq)} to {num(result.head_seq)}
        {result.unchained ? ` · ${num(result.unchained)} rows predate the chain (never sealed, not counted as breaks)` : ''}
        {result.anchors?.length ? ` · ${result.anchors.filter(a => a.ok).length}/${result.anchors.length} anchors reconcile` : ''}
      </p>
      {result.breaks?.length > 0 && (
        <table className="w-full text-[11px] mt-2" data-testid={`verify-breaks-${result.collection}`}>
          <thead><tr style={{ color: C.slate }}>
            <th className="text-left py-1">Position</th><th className="text-left py-1">Finding</th><th className="text-left py-1">Detail</th>
          </tr></thead>
          <tbody>
            {result.breaks.map((b, i) => (
              <tr key={i} style={{ borderTop: '1px solid rgba(136,150,168,0.15)' }}>
                <td className="py-1 font-mono">{num(b.seq)}</td>
                <td className="py-1 font-semibold" style={{ color: C.red }}>{b.reason.replace(/_/g, ' ')}</td>
                <td className="py-1" style={{ color: C.navy }}>{b.detail}{b.action ? ` (${b.action})` : ''}</td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
      {result.breaks_capped && <p className="text-[10px] mt-1" style={{ color: C.red }}>Stopped after the first {result.break_count} findings.</p>}
    </div>
  );
}

function CollectionCard({ api, row, isAdmin, onChanged }) {
  const [busy, setBusy] = useState('');
  const [reason, setReason] = useState('');
  const [result, setResult] = useState(null);
  const unsealed = row.unchained > 0;

  const verify = async () => {
    setBusy('verify');
    try {
      const { data } = await api.post('/audit-chain/verify', { collection: row.collection, limit: 0 });
      setResult(data);
      data.intact ? toast.success(`${row.label}: chain reconciles`) : toast.error(`${row.label}: ${data.break_count} rows do not reconcile`);
    } catch (e) { toast.error(e?.response?.data?.detail || 'Verification failed'); }
    setBusy('');
  };

  const seal = async () => {
    setBusy('seal');
    try {
      const { data } = await api.post('/audit-chain/seal', { collection: row.collection, reason });
      toast.success(`${num(data.sealed)} rows sealed — head at position ${num(data.head_seq)}`);
      setReason('');
      onChanged();
    } catch (e) { toast.error(e?.response?.data?.detail || 'Seal failed'); }
    setBusy('');
  };

  return (
    <Card className="rounded-md bg-white p-4" data-testid={`chain-card-${row.collection}`}
      style={{ border: '1px solid rgba(136,150,168,0.12)', borderLeft: `3px solid ${unsealed ? C.amber : C.green}` }}>
      <div className="flex items-center gap-2 mb-2">
        <Link2 className="w-4 h-4" style={{ color: unsealed ? C.amber : C.green }} />
        <h3 className="text-xs font-semibold uppercase tracking-wider" style={{ color: C.slate }}>{row.label}</h3>
        <span className="font-mono text-[10px]" style={{ color: C.slate }}>{row.collection}</span>
        <Chip testid={`chain-state-${row.collection}`}
          text={unsealed ? `${num(row.unchained)} rows not sealed` : 'fully sealed'}
          color={unsealed ? C.amber : C.green} />
      </div>

      <div className="grid grid-cols-5 gap-3 mb-2">
        <Stat label="Rows" value={num(row.total_rows)} testid={`chain-total-${row.collection}`} />
        <Stat label="Sealed" value={num(row.chained)} color={C.green} testid={`chain-sealed-${row.collection}`} />
        <Stat label="Not sealed" value={num(row.unchained)} color={row.unchained ? C.amber : C.slate} testid={`chain-unsealed-${row.collection}`} />
        <Stat label="Head position" value={num(row.head_seq)} testid={`chain-head-${row.collection}`} />
        <Stat label="Anchors" value={num(row.anchor_count)} testid={`chain-anchors-${row.collection}`} />
      </div>

      <p className="text-[11px] mb-1" style={{ color: C.slate }}>
        Head hash <Hash value={row.head_hash} testid={`chain-head-hash-${row.collection}`} />
        {row.head_day ? ` · last sealed ${row.head_day}` : ''}
        {row.sealed_retrospectively ? ` · ${num(row.sealed_retrospectively)} sealed retrospectively` : ''}
      </p>
      {row.last_anchor && (
        <p className="text-[11px]" style={{ color: C.slate }} data-testid={`chain-last-anchor-${row.collection}`}>
          Last anchor: {row.last_anchor.day} · {String(row.last_anchor.kind).replace(/_/g, ' ')} · {num(row.last_anchor.rows)} rows · <Hash value={row.last_anchor.anchor_hash} />
        </p>
      )}

      {isAdmin && (
        <div className="flex items-center gap-2 mt-3 flex-wrap">
          <Button size="sm" onClick={verify} disabled={!!busy} className="text-xs rounded-sm"
            style={{ background: C.navy }} data-testid={`verify-btn-${row.collection}`}>
            {busy === 'verify' ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : 'Verify chain'}
          </Button>
          {unsealed && (
            <>
              <Input placeholder="Basis for the seal (10+ chars)" value={reason} onChange={e => setReason(e.target.value)}
                className="text-xs rounded-sm max-w-sm" data-testid={`seal-reason-${row.collection}`} />
              <Button size="sm" variant="outline" onClick={seal} disabled={!!busy || reason.trim().length < 10}
                className="text-xs rounded-sm" data-testid={`seal-btn-${row.collection}`}>
                {busy === 'seal' ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <><Lock className="w-3.5 h-3.5 mr-1" />Seal {num(row.unchained)} earlier rows</>}
              </Button>
            </>
          )}
        </div>
      )}
      {result && <VerifyResult result={result} />}
    </Card>
  );
}

function LedgerCard({ api, led, isAdmin }) {
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState(null);
  const verify = async () => {
    setBusy(true);
    try {
      const { data } = await api.post('/audit-chain/verify', { collection: led.collection, limit: 0 });
      setResult(data);
      data.intact ? toast.success('Decision ledger: chain reconciles') : toast.error(`Decision ledger: ${data.break_count} rows do not reconcile`);
    } catch (e) { toast.error(e?.response?.data?.detail || 'Verification failed'); }
    setBusy(false);
  };
  return (
    <Card className="rounded-md bg-white p-4" data-testid="chain-card-decision_events"
      style={{ border: '1px solid rgba(136,150,168,0.12)', borderLeft: `3px solid ${C.blue}` }}>
      <div className="flex items-center gap-2 mb-2">
        <Link2 className="w-4 h-4" style={{ color: C.blue }} />
        <h3 className="text-xs font-semibold uppercase tracking-wider" style={{ color: C.slate }}>Decision ledger</h3>
        <span className="font-mono text-[10px]" style={{ color: C.slate }}>{led.collection}</span>
        <Chip testid="chain-state-decision_events" text={led.append_only_at_accessor ? 'append-only at the accessor' : 'mutable'}
          color={led.append_only_at_accessor ? C.green : C.red} />
      </div>
      <div className="grid grid-cols-5 gap-3 mb-2">
        <Stat label="Events" value={num(led.total_rows)} testid="chain-total-decision_events" />
        <Stat label="Head position" value={num(led.head_seq)} testid="chain-head-decision_events" />
      </div>
      <p className="text-[11px] mb-1" style={{ color: C.slate }}>
        Head hash <Hash value={led.head_hash} testid="chain-head-hash-decision_events" /> · {led.label}
      </p>
      {isAdmin && (
        <Button size="sm" onClick={verify} disabled={busy} className="text-xs rounded-sm mt-3"
          style={{ background: C.navy }} data-testid="verify-btn-decision_events">
          {busy ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : 'Verify chain'}
        </Button>
      )}
      {result && <VerifyResult result={result} />}
    </Card>
  );
}

function VerifyRunsCard({ api, last, isAdmin, onChanged }) {
  const [busy, setBusy] = useState(false);
  const [runs, setRuns] = useState([]);
  useEffect(() => {
    api.get('/audit-chain/verify-runs', { params: { limit: 5 } }).then(r => setRuns(r.data.runs || [])).catch(() => {});
  }, [api, last]);
  const verifyAll = async () => {
    setBusy(true);
    try {
      const { data } = await api.post('/audit-chain/verify-all');
      data.intact ? toast.success(`Every evidence store reconciles — run ${data.run_id}`) : toast.error(`Run ${data.run_id}: a store does not reconcile`);
      onChanged();
    } catch (e) { toast.error(e?.response?.data?.detail || 'Verify-all failed'); }
    setBusy(false);
  };
  return (
    <Card className="rounded-md bg-white p-4" style={{ border: '1px solid rgba(136,150,168,0.12)' }} data-testid="verify-runs-card">
      <div className="flex items-center gap-2 mb-2">
        <ShieldCheck className="w-4 h-4" style={{ color: C.green }} />
        <h3 className="text-xs font-semibold uppercase tracking-wider" style={{ color: C.slate }}>Verify runs — every store, daily</h3>
        {isAdmin && (
          <Button size="sm" onClick={verifyAll} disabled={busy} className="text-xs rounded-sm ml-auto"
            style={{ background: C.navy }} data-testid="verify-all-btn">
            {busy ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : 'Verify everything now'}
          </Button>
        )}
      </div>
      <p className="text-[11px] mb-2" style={{ color: C.slate }}>
        The three chained collections and the decision ledger are replayed together once a day by the anchor worker and on demand here;
        each run is kept with its verdict per store.
      </p>
      {last ? (
        <p className="text-[11px] mb-2" style={{ color: C.navy }} data-testid="last-verify-run">
          Last run <span className="font-mono">{last.run_id}</span> · {String(last.at || '').slice(0, 16).replace('T', ' ')} UTC · {last.by} ·{' '}
          <Chip testid="last-verify-verdict" text={last.intact ? 'intact' : 'NOT intact'} color={last.intact ? C.green : C.red} />
          {' '}· ledger {num(last.decision_ledger?.rows_checked)} rows
        </p>
      ) : <p className="text-[11px] mb-2" style={{ color: C.amber }} data-testid="last-verify-run">No verify run on file yet.</p>}
      {runs.length > 0 && (
        <table className="w-full text-[11px]" data-testid="verify-runs-table">
          <thead><tr style={{ color: C.slate }}>
            <th className="text-left py-1">Run</th><th className="text-left py-1">At (UTC)</th><th className="text-left py-1">By</th><th className="text-left py-1">Verdict</th>
          </tr></thead>
          <tbody>
            {runs.map(r => (
              <tr key={r.run_id} style={{ borderTop: '1px solid rgba(136,150,168,0.15)' }} data-testid={`verify-run-${r.run_id}`}>
                <td className="py-1 font-mono">{r.run_id}</td>
                <td className="py-1">{String(r.at || '').slice(0, 16).replace('T', ' ')}</td>
                <td className="py-1" style={{ color: C.slate }}>{r.by}</td>
                <td className="py-1"><Chip text={r.intact ? 'intact' : 'not intact'} color={r.intact ? C.green : C.red} /></td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
    </Card>
  );
}

export default function AuditChainPage() {
  const api = useApi();
  const { user } = useAuth();
  const isAdmin = ['platform_owner', 'mga_admin'].includes(user?.role);
  const [state, setState] = useState(null);
  const [anchors, setAnchors] = useState([]);
  const [loading, setLoading] = useState(true);

  const load = useCallback(() => {
    setLoading(true);
    Promise.all([api.get('/audit-chain/status'), api.get('/audit-chain/anchors', { params: { limit: 50 } })])
      .then(([s, a]) => { setState(s.data); setAnchors(a.data.anchors || []); })
      .catch(() => toast.error('Failed to load the audit chain'))
      .finally(() => setLoading(false));
  }, [api]);

  useEffect(() => { load(); }, [load]);

  const exportCsv = async () => {
    try {
      const res = await api.get('/audit-chain/anchors.csv', { responseType: 'blob' });
      const url = URL.createObjectURL(new Blob([res.data], { type: 'text/csv' }));
      const a = document.createElement('a');
      a.href = url; a.download = 'audit_chain_anchors.csv'; a.click();
      URL.revokeObjectURL(url);
    } catch { toast.error('Export failed'); }
  };

  if (loading && !state) return <div className="p-8 text-sm" style={{ color: C.slate }}>Loading the audit chain…</div>;
  if (!state) return null;

  return (
    <div className="space-y-4" data-testid="audit-chain-page">
      <p className="text-xs" style={{ color: C.slate }}>
        Every row in these three collections is sealed with sha256 over its own content plus the hash of the row before it, so the log is a chain —
        change a row, remove one, or slip one in, and every hash after it stops reconciling. Sealing happens at one choke point, so nothing writes to
        them unsealed. <b style={{ color: C.navy }}>Fail closed:</b> a row that cannot be sealed is refused, and so is the action that asked for it.
        update, delete and drop are refused outright — a correction is a new row, never a change to an old one.
      </p>

      {state.collections.map(row => (
        <CollectionCard key={row.collection} api={api} row={row} isAdmin={isAdmin} onChanged={load} />
      ))}
      {state.decision_ledger && <LedgerCard api={api} led={state.decision_ledger} isAdmin={isAdmin} />}
      <VerifyRunsCard api={api} last={state.last_verify} isAdmin={isAdmin} onChanged={load} />

      <Card className="rounded-md bg-white p-4" style={{ border: '1px solid rgba(136,150,168,0.12)' }} data-testid="anchors-card">
        <div className="flex items-center gap-2 mb-2">
          <Anchor className="w-4 h-4" style={{ color: C.blue }} />
          <h3 className="text-xs font-semibold uppercase tracking-wider" style={{ color: C.slate }}>Daily anchors</h3>
          <Button size="sm" variant="outline" onClick={exportCsv} className="text-xs rounded-sm ml-auto" data-testid="anchors-export-btn">
            <Download className="w-3.5 h-3.5 mr-1" />Export CSV
          </Button>
        </div>
        <p className="text-[11px] mb-2" style={{ color: C.slate }}>
          A closed day is anchored with its first and last position, its row count and the head hash, and each anchor carries the hash of the anchor
          before it. A day can be checked from its anchor alone. A retrospective seal proves nothing changes from the seal date forward, and nothing
          about what happened before it — the basis on each row says which it is.
        </p>
        {anchors.length === 0 ? (
          <p className="text-xs" style={{ color: C.slate }} data-testid="anchors-empty">No day has closed since the chain started. The first anchor seals tomorrow.</p>
        ) : (
          <table className="w-full text-[11px]" data-testid="anchors-table">
            <thead><tr style={{ color: C.slate }}>
              <th className="text-left py-1">Day</th><th className="text-left py-1">Collection</th><th className="text-left py-1">Kind</th>
              <th className="text-right py-1">Rows</th><th className="text-right py-1">Positions</th>
              <th className="text-left py-1">Anchor hash</th><th className="text-left py-1">Officer</th>
            </tr></thead>
            <tbody>
              {anchors.map(a => (
                <tr key={`${a.collection}-${a.anchor_seq}`} style={{ borderTop: '1px solid rgba(136,150,168,0.15)' }}
                  data-testid={`anchor-row-${a.collection}-${a.anchor_seq}`}>
                  <td className="py-1 font-mono">{a.day}</td>
                  <td className="py-1">{a.collection}</td>
                  <td className="py-1">
                    <Chip text={String(a.kind).replace(/_/g, ' ')} color={a.kind === 'daily' ? C.green : a.kind === 'chain_reset' ? C.red : C.amber} />
                  </td>
                  <td className="py-1 text-right font-mono">{num(a.rows)}</td>
                  <td className="py-1 text-right font-mono pr-4">{num(a.first_seq)}–{num(a.last_seq)}</td>
                  <td className="py-1"><Hash value={a.anchor_hash} /></td>
                  <td className="py-1" style={{ color: C.slate }}>{a.officer || '—'}</td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </Card>
    </div>
  );
}
